import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

const EXPECTED = [
  "contact_id", "email", "first_name", "last_name", "una_name", "suite_number",
  "f2903", "f2967", "f3113", "f3134",
];

type Row = Record<string, string>;

type RowResult = {
  row: number;
  email: string;
  contact_id: string;
  outcome: string;
  linked: boolean;
  suite_conflict: boolean;
  detail: string;
};

type RunReport = {
  dry_run: boolean;
  counts: Record<string, number>;
  results: RowResult[];
};

/** Small CSV reader that handles quoted values and commas inside quotes. */
function parseCsv(input: string): { headers: string[]; rows: Row[] } {
  const text = input.replace(/\r\n?/g, "\n").trim();
  if (!text) return { headers: [], rows: [] };

  const records: string[][] = [];
  let field = "";
  let record: string[] = [];
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { quoted = false; }
      } else field += c;
      continue;
    }
    if (c === '"') { quoted = true; continue; }
    if (c === ",") { record.push(field); field = ""; continue; }
    if (c === "\n") { record.push(field); records.push(record); record = []; field = ""; continue; }
    field += c;
  }
  record.push(field);
  records.push(record);

  const headerRow = (records.shift() || []).map((h) => h.trim());
  const keys = headerRow.map((h) => h.toLowerCase());
  const rows: Row[] = records
    .filter((r) => r.some((v) => v.trim() !== ""))
    .map((r) => {
      const obj: Row = {};
      keys.forEach((k, idx) => { obj[k] = (r[idx] ?? "").trim(); });
      return obj;
    });

  return { headers: headerRow, rows };
}

export default function LegacyImport() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [report, setReport] = useState<RunReport | null>(null);

  const parsed = useMemo(() => parseCsv(raw), [raw]);
  const found = parsed.headers.map((h) => h.toLowerCase());
  const missing = EXPECTED.filter((k) => !found.includes(k));

  const onFile = async (file: File | null) => {
    if (!file) return;
    setRaw(await file.text());
    setReport(null);
  };

  const run = async (dryRun: boolean) => {
    if (parsed.rows.length === 0) {
      toast.error("There are no rows to read yet.");
      return;
    }
    setBusy(true);
    setReport(null);
    try {
      const { data, error } = await supabase.functions.invoke("import-legacy-members", {
        body: { dry_run: dryRun, rows: parsed.rows },
      });
      if (error) {
        let message = error.message;
        try {
          const body = await (error as unknown as { context?: Response }).context?.json();
          if (body?.error) message = body.error;
        } catch { /* keep the original message */ }
        throw new Error(message);
      }
      setReport(data as RunReport);
      toast.success(dryRun ? "Dry run finished." : "Import finished.");
      if (!dryRun) setConfirmText("");
    } catch (e) {
      toast.error((e as Error).message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;
    const header = "row,email,contact_id,outcome,linked,suite_conflict,detail\n";
    const body = report.results
      .map((r) =>
        [r.row, r.email, r.contact_id, r.outcome, r.linked, r.suite_conflict, `"${(r.detail || "").replace(/"/g, '""')}"`].join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(new Blob([header + body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `legacy-import-${report.dry_run ? "dryrun" : "run"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Legacy Import</h1>
        <p className="text-sm text-muted-foreground">
          One time import of the members who joined before member records existed. This writes to
          the portal only. It never sends anything to Ontraport.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Paste or upload the export</CardTitle>
          <CardDescription>
            Expected columns, in any order: {EXPECTED.join(", ")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="legacy-file">CSV file</Label>
            <Input
              id="legacy-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="legacy-csv">Or paste the CSV here</Label>
            <Textarea
              id="legacy-csv"
              rows={8}
              value={raw}
              onChange={(e) => { setRaw(e.target.value); setReport(null); }}
              placeholder="contact_id,email,first_name,last_name,..."
            />
          </div>
        </CardContent>
      </Card>

      {parsed.rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              {parsed.rows.length} rows found. Showing the first 10.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {EXPECTED.map((k) => (
                <Badge key={k} variant={found.includes(k) ? "default" : "outline"}>
                  {k} {found.includes(k) ? "found" : "not found"}
                </Badge>
              ))}
            </div>
            {missing.includes("email") && missing.includes("contact_id") && (
              <p className="text-sm text-destructive">
                This file needs an email column or a contact id column.
              </p>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {parsed.headers.map((h) => <TableHead key={h}>{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsed.rows.slice(0, 10).map((r, i) => (
                    <TableRow key={i}>
                      {parsed.headers.map((h) => (
                        <TableCell key={h} className="max-w-[220px] truncate">
                          {r[h.toLowerCase()]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <Button onClick={() => run(true)} disabled={busy} variant="outline">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Dry run
              </Button>
              <div className="space-y-2">
                <Label htmlFor="confirm">Type IMPORT to run for real</Label>
                <Input
                  id="confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="md:w-48"
                />
              </div>
              <Button onClick={() => run(false)} disabled={busy || confirmText !== "IMPORT"}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Run import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {report && (
        <Card>
          <CardHeader>
            <CardTitle>{report.dry_run ? "Dry run report" : "Import report"}</CardTitle>
            <CardDescription>
              Created {report.counts.created}, linked to an account {report.counts.linked},
              updated {report.counts.updated}, skipped {report.counts.skipped},
              suite conflicts {report.counts.suite_conflicts}, errors {report.counts.errors}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={downloadReport}>Download the report</Button>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Contact id</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Account linked</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.results.map((r) => (
                    <TableRow key={r.row}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.contact_id}</TableCell>
                      <TableCell>
                        <Badge variant={r.outcome === "error" ? "destructive" : "secondary"}>
                          {r.outcome}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.linked ? "Yes" : "No"}</TableCell>
                      <TableCell className="max-w-[360px]">{r.detail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
